import * as React from "react";
import { ScaVerify } from "../ScaAirwallex/ScaVerify";
import {
  SCA_VERIFY_EVENTS,
  dispatchScaVerifyEvent,
} from "./scaVerifyEvents";

/**
 * Embeddable ScaVerify for APP flows (Add Recipient, Create Transfer, View full card details, View Pin, etc.).
 * - Supports all existing ScaVerify callback props.
 * - Dispatches custom DOM events (sca-verify:success, sca-verify:error, etc.) so the host can listen
 *   without passing callbacks (e.g. native app via WebView).
 *
 * Usage in a flow:
 *   <ScaVerifyEmbedded
 *     scaSessionCode={scaSessionCode}
 *     onVerificationSucceed={() => setStep(nextStep)}
 *     onVerificationFailed={() => setStep(errorStep)}
 *   />
 *
 * APP listening to events (e.g. in WebView or parent page):
 *   window.addEventListener('sca-verify:success', (e) => { const { token } = e.detail; ... });
 *   window.addEventListener('sca-verify:failed', (e) => { const { reason } = e.detail; ... });
 *   window.addEventListener('sca-verify:error', (e) => { ... e.detail });
 *   window.addEventListener('sca-verify:cancel', () => { ... });
 */
export function ScaVerifyEmbedded({
  scaSessionCode,
  onReady,
  onVerificationSucceed,
  onVerificationFailed,
  onError,
  onCancel,
  onScaSetupSucceed,
  ...rest
}) {
  const handleReady = React.useCallback(
    (e) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.READY, {});
      onReady?.(e);
    },
    [onReady]
  );

  const handleVerificationSucceed = React.useCallback(
    ({ token }) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.SUCCESS, { token });
      onVerificationSucceed?.({ token });
    },
    [onVerificationSucceed]
  );

  const handleVerificationFailed = React.useCallback(
    ({ reason }) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.FAILED, { reason });
      onVerificationFailed?.({ reason });
    },
    [onVerificationFailed]
  );

  const handleError = React.useCallback(
    (error) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.ERROR, {
        message: error?.message,
        error,
      });
      onError?.(error);
    },
    [onError]
  );

  const handleCancel = React.useCallback(
    (event) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.CANCEL, { event });
      onCancel?.(event);
    },
    [onCancel]
  );

  const handleScaSetupSucceed = React.useCallback(
    ({ mobileInfo }) => {
      dispatchScaVerifyEvent(SCA_VERIFY_EVENTS.SCA_SETUP_SUCCEED, {
        mobileInfo,
      });
      onScaSetupSucceed?.({ mobileInfo });
    },
    [onScaSetupSucceed]
  );

  return (
    <ScaVerify
      scaSessionCode={scaSessionCode}
      onReady={handleReady}
      onVerificationSucceed={handleVerificationSucceed}
      onVerificationFailed={handleVerificationFailed}
      onError={handleError}
      onCancel={handleCancel}
      onScaSetupSucceed={handleScaSetupSucceed}
      {...rest}
    />
  );
}
